/**
 * Parse contentful api response and return required fields
 * @param {any} response Contentful api response
 */
export const contentfulParser = (response) => {
  const data = {};
  // Fallback response if api call fail
  if (!response) {
    data.logo = {
      title: 'Spyscape',
      image: {
        title: 'Spyscape',
        file: {
          url: '',
        },
      },
    };
    data.topRightItems = [];
    data.secondaryMenu = [];
    data.exploreMenuExpandable = {
      items: [],
      subItems: [],
    };
    data.includeTicketsButton = false;
    data.includeShopButton = false;
    data.alternateNavbarTheme = false;

    return data;
  }

  response.items.forEach((item) => {
    data.logo = parseLogo(response, item);
    data.topRightItems = parseTopRightItem(response, item);
    data.secondaryMenu = parseSecondaryMenu(response, item);

    data.exploreMenuExpandable = parseExploreMenu(response, item);
    data.includeTicketsButton = item.fields.includeTicketsButton;
    data.includeShopButton = item.fields.includeShopButton;
    data.alternateNavbarTheme = item.fields.alternateNavbarTheme;

    if (item.fields.useImageInExploreMenu) {
      data.useImageInExploreMenu = item.fields.useImageInExploreMenu;
    }
    if (item.fields.primaryColor) {
      data.primaryColor = item.fields.primaryColor;
    }
  });

  return data;
};


/**
 * Parse contentful response and return logo
 * @param {any} response Contentful response
 * @param {any} item Contentful response field
 */
const parseLogo = (response, item) => {
  if (item.fields.logo) {
    const logo = getItemFields(response, item.fields.logo.sys.id);
    if (logo.image.sys) {
      logo.image = getAssetImage(response, logo.image.sys.id);
    }
    return logo;
  }
  // Fallback logo
  return {
    title: 'Spyscape',
    image: {
      title: 'Spyscape',
      file: {
        url: '',
      },
    },
  };
};

/**
 * Parse contentful response and return topRigthItem (PrimaryNavigationButton)
 * @param {any} response Contentful response
 * @param {any} item Contentful response field
 */
const parseTopRightItem = (response, item) => {
  const topRightItems = [];
  if (item.fields.topRightItem) {
    item.fields.topRightItem.forEach((field) => {
      const itemField = getItemFields(response, field.sys.id);
      if (itemField.subItems) {
        itemField.children = getSubItemFields(response, itemField);
      }
      if (itemField.additionalItems) {
        itemField.additionalItems = getAdditionalItemFields(response, itemField);
      }
      topRightItems.push(itemField);
    });
  }
  return topRightItems;
};

/**
 * Parse contentful response and return secondary menu
 * @param {any} response Contentful response
 * @param {any} item Contentful response field
 */
const parseSecondaryMenu = (response, item) => {
  const secondaryMenu = [];
  if (item.fields.secondaryMenu) {
    item.fields.secondaryMenu.forEach((field) => {
      const itemField = getItemFields(response, field.sys.id);
      if (itemField && itemField.subItems) {
        itemField.children = getSubItemFields(response, itemField);
      }
      if (itemField && itemField.additionalItems) {
        itemField.additionalItems = getAdditionalItemFields(response, itemField);
      }
      secondaryMenu.push(itemField);
    });
  }
  return secondaryMenu;
};

const parseExploreMenuItem = response => (field) => {
  const itemField = getItemFields(response, field.sys.id);
  const coverId = itemField.coverImage?.sys?.id;
  const imageId = itemField.image?.sys?.id;

  return {
    ...itemField,
    ...coverId && { coverImage: getAssetImage(response, coverId) },
    ...imageId && { image: getAssetImage(response, imageId) },
  };
};

/**
 * Parse contentful response and return explore menu
 * @param {any} response Contentful response
 * @param {any} items Parsed Contentful response fields
 */
const parseExploreMenuItems = (response, items) => {
  if (!items) return [];

  return items.map(parseExploreMenuItem(response));
};

/**
 * Parse contentful response and return explore menu
 * @param {any} response Contentful response
 * @param {any} item Contentful response field
 */
const parseExploreMenu = (response, { fields: { exploreMenuExpandable } }) => {
  if (!exploreMenuExpandable) {
    return {
      items: [],
      subItems: [],
    };
  }
  const itemFields = getItemFields(response, exploreMenuExpandable.sys.id);

  return {
    items: parseExploreMenuItems(response, itemFields.items),
    subMenuButtonLabel: itemFields.subMenuButtonLabel,
    openSubMenuButtonLabel: itemFields.openSubMenuButtonLabel,
    subItems: parseExploreMenuItems(response, itemFields.subItems),
  };
};

/**
 * Parse contentful response subSites and return active site id
 * @param {any} response Contentful response
 */
export const getActiveSite = (response) => {
  let activeSiteId;
  response.items.forEach((item) => {
    if (item.fields.subSites) {
      item.fields.subSites.forEach((field) => {
        const itemField = getItemFields(response, field.sys.id);
        if (!itemField || !itemField.id) return;
        itemField.id = field.sys.id;

        if (itemField.slugs) {
          const { pathname } = window.location;
          if (itemField.slugs.findIndex(e => pathname.startsWith(e)) > -1) {
            activeSiteId = field.sys.id;
          }
        }
      });
    }
  });
  return activeSiteId;
};

/**
 * Return asset image from contentful response by field sys id
 * @param {any} response Contetful response
 * @param {any} itemId Conteful field item sys id
 */
const getAssetImage = (response, itemId) => {
  const obj = response.includes.Asset.find(e => e.sys.id === itemId);
  if (obj) {
    return obj.fields;
  }
  return undefined;
};

/**
 * Return fields from contentful response by field sys id
 * @param {any} response Contetful response
 * @param {any} itemId Conteful field item sys id
 */
const getItemFields = (response, itemId) => {
  const obj = response.includes.Entry.find(e => e.sys.id === itemId);
  if (obj) {
    obj.id = itemId;
    return obj.fields;
  }
  return undefined;
};

/**
 * Return sub fields from contentful response by field sys id
 * @param {any} response Contetful response
 * @param {any} itemId Conteful field item sys id
 */
const getSubItemFields = (response, menuItem) => {
  const children = [];

  menuItem.subItems.forEach((item) => {
    const obj = getItemFields(response, item.sys.id);
    obj.id = item.sys.id;
    children.push(obj);
  });

  return children;
};

/**
 * Return additional item fields from contentful response by field sys id
 * @param {any} response Contetful response
 * @param {any} itemId Conteful field item sys id
 */
const getAdditionalItemFields = (response, menuItem) => {
  const children = [];

  menuItem.additionalItems.forEach((item) => {
    if (item.sys) {
      const obj = getItemFields(response, item.sys.id);
      children.push(obj);
    }
  });

  return children;
};
